import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import CustomDatePicker from '../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import { XtramButton, XtramSelect } from '../Widgets/XtramModal/XtramModal';
import styles from './BankXtram.module.scss';
import '../../styles/bankTokens.css';

export const PERIOD_MODE = { YEAR: 'year', RANGE: 'range' };

/** How far back the year list goes; older years stay reachable via range mode. */
const YEARS_BACK = 10;

/**
 * Rows are stored with UTC timestamps, so every bound is built in UTC to avoid
 * slipping a day for users in negative-offset timezones.
 */
export const yearBounds = (year) => ({
    start: new Date(Date.UTC(year, 0, 1)),
    end: new Date(Date.UTC(year, 11, 31, 23, 59, 59)),
});

export const startOfUtcDay = (date) =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

export const endOfUtcDay = (date) =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59));

/**
 * The period a bank screen is scoped to, in one of two shapes: a whole calendar
 * year, or an explicit start/end range. Both screens share this so the same
 * account always reports the same figures for the same period.
 */
const BankPeriodPicker = ({
    mode = PERIOD_MODE.YEAR,
    onModeChange,
    year,
    onYearChange,
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    disabled = false,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        const list = [];
        for (let y = current + 1; y >= current - YEARS_BACK; y -= 1) list.push(y);
        if (year != null && !list.includes(year)) {
            list.push(year);
            list.sort((a, b) => b - a);
        }
        return list.map((value) => ({ value, label: String(value) }));
    }, [year]);

    return (
        <div className={styles.periodPicker}>
            <div className={styles.segmented}>
                <XtramButton
                    variant="ghost"
                    small
                    active={mode === PERIOD_MODE.YEAR}
                    disabled={disabled}
                    onClick={() => mode !== PERIOD_MODE.YEAR && onModeChange?.(PERIOD_MODE.YEAR)}
                >
                    {t('Year')}
                </XtramButton>
                <XtramButton
                    variant="ghost"
                    small
                    active={mode === PERIOD_MODE.RANGE}
                    disabled={disabled}
                    onClick={() => mode !== PERIOD_MODE.RANGE && onModeChange?.(PERIOD_MODE.RANGE)}
                >
                    {t('DateRange')}
                </XtramButton>
            </div>

            {mode === PERIOD_MODE.YEAR ? (
                <div className={styles.yearSelect}>
                    <XtramSelect
                        direction={direction}
                        options={yearOptions}
                        value={yearOptions.find((option) => option.value === year) || null}
                        onChange={(option) => option && onYearChange?.(option.value)}
                        placeholder={t('Year')}
                        disabled={disabled}
                        disableClearable
                    />
                </div>
            ) : (
                <>
                    <div className={styles.dateField}>
                        <CustomDatePicker
                            date={startDate}
                            onDateChange={(next) => next && onStartDateChange?.(next)}
                            label={t('StartDate')}
                            height="40px"
                            disabled={disabled}
                        />
                    </div>
                    <div className={styles.dateField}>
                        <CustomDatePicker
                            date={endDate}
                            onDateChange={(next) => next && onEndDateChange?.(next)}
                            label={t('EndDate')}
                            height="40px"
                            disabled={disabled}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default BankPeriodPicker;
